import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import SetCard from "@/components/SetCard";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

const baseBtn = "inline-flex items-center justify-center gap-1 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] border cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

export default function Profile() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [sets, setSets] = useState([]);
  const [following, setFollowing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [bioDraft, setBioDraft] = useState("");
  const [savingBio, setSavingBio] = useState(false);

  const load = () => {
    api.get(`/users/${id}`).then(({ data }) => setProfile(data));
    api.get(`/sets?author_id=${id}`).then(({ data }) => setSets(data));
  };

  useEffect(load, [id]);

  const toggleFollow = async () => {
    if (!user) { toast.error("Login to follow"); return; }
    const { data } = await api.post(`/users/${id}/follow`);
    setFollowing(data.following);
    setProfile({ ...profile, followers_count: profile.followers_count + (data.following ? 1 : -1) });
  };

  const openEdit = () => { setBioDraft(profile.bio || ""); setEditing(true); };
  const saveBio = async () => {
    setSavingBio(true);
    try {
      const { data } = await api.patch("/users/me", { bio: bioDraft });
      setProfile({ ...profile, bio: data.bio });
      toast.success("Profile updated");
      setEditing(false);
      try { refresh?.(); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save bio");
    } finally { setSavingBio(false); }
  };

  if (!profile) return <div className="min-h-[60vh] flex items-center justify-center font-mono text-xs uppercase tracking-widest">Loading…</div>;
  const isMe = user?.id === profile.id;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="border-b border-[#E5E5E5] pb-10 mb-10 flex flex-col md:flex-row gap-8 items-start">
        <div className="w-32 h-32 bg-[#333333] text-white flex items-center justify-center font-heading text-5xl">
          {profile.username[0]?.toUpperCase()}
        </div>
        <div className="flex-1 w-full">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA47E]">◇ {profile.plan} member</div>
          <h1 className="font-heading text-5xl mt-3" data-testid="profile-username">@{profile.username}</h1>

          {/* About me */}
          <div className="mt-6 max-w-2xl" data-testid="profile-about">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA47E]">§ About me</div>
              {isMe && (
                <button
                  type="button"
                  data-testid="edit-bio-btn"
                  onClick={openEdit}
                  className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#595959] hover:text-[#9CA47E] cursor-pointer inline-flex items-center gap-1"
                >
                  <Pencil className="w-3 h-3" /> {profile.bio ? "Edit bio" : "Add bio"}
                </button>
              )}
            </div>
            {profile.bio ? (
              <p className="font-body text-[#333333] leading-relaxed whitespace-pre-line" data-testid="profile-bio">
                {profile.bio}
              </p>
            ) : (
              <p className="font-body text-sm text-[#A0A0A0] italic" data-testid="profile-bio-empty">
                {isMe
                  ? "Tell the collective who you are — your style, what you're collecting, where you find inspiration."
                  : "This curator hasn't written a bio yet."}
              </p>
            )}
          </div>

          <div className="flex gap-8 mt-6 font-mono text-xs uppercase tracking-widest">
            <div><span className="font-heading text-2xl text-[#333333]">{profile.sets_count}</span><div className="text-[#595959] mt-1">Sets</div></div>
            <div><span className="font-heading text-2xl text-[#333333]">{profile.followers_count}</span><div className="text-[#595959] mt-1">Followers</div></div>
            <div><span className="font-heading text-2xl text-[#333333]">{profile.following_count}</span><div className="text-[#595959] mt-1">Following</div></div>
          </div>
          {!isMe && (
            <Button data-testid="follow-btn" onClick={toggleFollow} className={`rounded-none mt-6 ${following ? "bg-white border border-[#333333] text-[#333333] hover:bg-[#F2EDE3]" : "bg-[#333333] text-white hover:bg-[#333]"}`}>
              {following ? "Following" : "Follow"}
            </Button>
          )}
        </div>
      </div>

      <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#9CA47E] mb-6">§ Sets</div>
      {sets.length === 0 ? (
        <div className="font-body text-[#595959] py-20 text-center">No sets yet.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {sets.map((s) => <SetCard key={s.id} set={s} />)}
        </div>
      )}

      {/* Edit bio dialog */}
      <Dialog open={editing} onOpenChange={(o) => !o && setEditing(false)}>
        <DialogContent className="rounded-none border-[#E5E5E5]" data-testid="edit-bio-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit your bio</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#595959]">
              About me · 1000 characters max
            </div>
            <textarea
              data-testid="edit-bio-textarea"
              value={bioDraft}
              onChange={(e) => setBioDraft(e.target.value.slice(0, 1000))}
              rows={6}
              placeholder="Tell the collective about yourself — your style, what you collect, where you find inspiration…"
              className="w-full border border-[#E5E5E5] focus:border-[#333333] focus:outline-none p-3 font-body text-sm bg-white resize-none"
              autoFocus
            />
            <div className="flex justify-end font-mono text-[9px] uppercase tracking-[0.22em] text-[#A0A0A0]">
              {bioDraft.length} / 1000
            </div>
          </div>
          <DialogFooter className="mt-4 gap-2">
            <button
              type="button"
              data-testid="edit-bio-cancel"
              onClick={() => setEditing(false)}
              className={`${baseBtn} border-[#E5E5E5] text-[#595959] hover:border-[#333333] hover:text-[#333333]`}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="edit-bio-save"
              onClick={saveBio}
              disabled={savingBio}
              className={`${baseBtn} border-[#9CA47E] bg-[#9CA47E] text-white hover:bg-[#7E876A]`}
            >
              {savingBio ? "Saving…" : "Save bio"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
